import {
	SelectActions,
	getActionFromKey,
	getIndexByLetter,
	getUpdatedIndex,
	isElementInView,
	isScrollable,
	maintainScrollVisibility,
} from './helpers';

let idCounter = 0;

/*
 * Select Component
 * Accepts a combobox element and an array of string options
 */
class Select {
	constructor(el) {
		// element refs
		this.el = el;
		this.labelEl = el.querySelector('.combo-label');
		this.comboEl = el.querySelector('[role=combobox]');
		this.listboxEl = el.querySelector('[role=listbox]');
		this.optionLabels = Array.from(this.listboxEl.children).map(
			(o) => o.textContent
		);

		// data
		idCounter += 1;
		this.idBase = this.comboEl.id || `combo-${idCounter}`;

		// state
		this.activeIndex = 0;
		this.open = false;
		this.searchString = '';
		this.searchTimeout = null;

		// init
		if (el && this.comboEl && this.listboxEl) {
			this.init();
		}
	}

	init() {
		// add event listeners
		this.labelEl?.addEventListener('click', this.onLabelClick.bind(this));
		this.comboEl.addEventListener('blur', this.onComboBlur.bind(this));
		this.listboxEl.addEventListener('focusout', this.onComboBlur.bind(this));
		this.comboEl.addEventListener('click', this.onComboClick.bind(this));
		this.comboEl.addEventListener('keydown', this.onComboKeyDown.bind(this));

		this.listboxEl
			.querySelectorAll('[role=option]')
			.forEach((optionEl, index) => {
				optionEl.id = `${this.idBase}-${index}`;
				optionEl.addEventListener('click', (event) => {
					event.stopPropagation();
					this.onOptionClick(index);
				});
				optionEl.addEventListener(
					'mousedown',
					this.onOptionMouseDown.bind(this)
				);
			});
	}

	getSearchString(char) {
		// reset typing timeout and start new timeout
		// this allows us to make multiple-letter matches, like a native select
		if (typeof this.searchTimeout === 'number') {
			window.clearTimeout(this.searchTimeout);
		}

		this.searchTimeout = window.setTimeout(() => {
			this.searchString = '';
		}, 500);

		// add most recent letter to saved search string
		this.searchString += char;
		return this.searchString;
	}

	onLabelClick() {
		this.comboEl.focus();
	}

	onComboBlur(event) {
		// do nothing if relatedTarget is contained within listboxEl
		if (this.listboxEl.contains(event.relatedTarget)) {
			return;
		}

		// select current option and close
		if (this.open) {
			this.selectOption(this.activeIndex);
			this.updateMenuState(false, false);
		}
	}

	onComboClick() {
		this.updateMenuState(!this.open, false);
	}

	onComboKeyDown(event) {
		const { key } = event;
		const max = this.optionLabels.length - 1;

		const action = getActionFromKey(event, this.open);

		switch (action) {
			case SelectActions.Last:
			case SelectActions.First:
				this.updateMenuState(true);
			// intentional fallthrough
			case SelectActions.Next:
			case SelectActions.Previous:
			case SelectActions.PageUp:
			case SelectActions.PageDown:
				event.preventDefault();
				return this.onOptionChange(
					getUpdatedIndex(this.activeIndex, max, action)
				);
			case SelectActions.CloseSelect:
				event.preventDefault();
				this.selectOption(this.activeIndex);
			// intentional fallthrough
			case SelectActions.Close:
				event.preventDefault();
				return this.updateMenuState(false);
			case SelectActions.Type:
				return this.onComboType(key);
			case SelectActions.Open:
				event.preventDefault();
				return this.updateMenuState(true);
			default:
		}
	}

	onComboType(letter) {
		// open the listbox if it is closed
		this.updateMenuState(true);

		// find the index of the first matching option
		const searchString = this.getSearchString(letter);
		const searchIndex = getIndexByLetter(
			this.optionLabels,
			searchString,
			this.activeIndex + 1
		);

		// if a match was found, go to it
		if (searchIndex >= 0) {
			this.onOptionChange(searchIndex);
		}

		// if no matches, clear the timeout and search string
		else {
			window.clearTimeout(this.searchTimeout);
			this.searchString = '';
		}
	}

	onOptionChange(index) {
		// update state
		this.activeIndex = index;

		// update aria-activedescendant
		this.comboEl.setAttribute(
			'aria-activedescendant',
			`${this.idBase}-${index}`
		);

		// update active option styles
		const options = this.el.querySelectorAll('[role=option]');

		options.forEach((optionEl, optIndex) => {
			optionEl.dataset.hasFocus = optIndex === index ? 'true' : 'false';
		});

		// ensure the new option is in view
		if (isScrollable(this.listboxEl)) {
			maintainScrollVisibility(options[index], this.listboxEl);
		}

		// ensure the new option is visible on screen
		// ensure the new option is in view
		if (!isElementInView(options[index])) {
			options[index].scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
			});
		}
	}

	onOptionClick(index) {
		this.onOptionChange(index);
		this.selectOption(index);
		this.updateMenuState(false);
	}

	onOptionMouseDown() {
		// Clicking an option will cause a blur event,
		// but we don't want to perform the default keyboard blur action
		this.ignoreBlur = true;
	}

	selectOption(index) {
		// update state
		this.activeIndex = index;

		// update displayed value
		const selected = this.optionLabels[index];
		this.comboEl.innerText = selected;

		// update aria-selected
		const options = this.el.querySelectorAll('[role=option]');
		[...options].forEach((optionEl) => {
			optionEl.setAttribute('aria-selected', 'false');
		});
		options[index].setAttribute('aria-selected', 'true');

		this.el.dispatchEvent(
			new CustomEvent('select', {
				detail: { selectedIndex: this.activeIndex + 1 },
			})
		);
	}

	updateMenuState(open, callFocus = true) {
		if (this.open === open) {
			return;
		}

		// update state
		this.open = open;

		// update aria-expanded and styles
		this.comboEl.setAttribute('aria-expanded', `${open}`);

		// update activedescendant
		const activeID = open ? `${this.idBase}-${this.activeIndex}` : '';
		this.comboEl.setAttribute('aria-activedescendant', activeID);

		if (activeID === '') {
			this.listboxEl.querySelectorAll('[role=option]').forEach((optionEl) => {
				optionEl.dataset.hasFocus = 'false';
			});
		}

		if (activeID === '' && !isElementInView(this.comboEl)) {
			this.comboEl.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
			});
		}

		// move focus back to the combobox, if needed
		callFocus && this.comboEl.focus();
	}
}

export default Select;
